import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import SaveIcon from '@mui/icons-material/Save';
import toast from 'react-hot-toast';
import PageHeader from '../../ui-components/PageHeader';
import { authFetch } from '../../utils/authHelpers';
import { BACKEND_URL } from '../../utils/apiConfig';
import { useUserRole } from '../../contexts/UserRoleContext';

/**
 * BBI-doel — het jaardoel voor de brutobijdrage waar de personeelsbonus aan hangt,
 * met de maandstanden uit Only's maandrapportage.
 *
 * Management vult 1× per maand het BBI-bedrag in (en eenmalig Only's maandbudget
 * als doelpad). De pagina rekent voortgang, achterstand en benodigd tempo uit;
 * de presentatie-sheet toont daarvan alleen wat het personeel mag zien: doel,
 * stand en tempo — geen EBITDA, personeelskosten of debiteuren.
 */
const FONT = '"Inter", system-ui, sans-serif';
const NAV_FONT = '"Manrope", system-ui, sans-serif';
const ACCENT = { bg: '#dcfce7', color: '#15803d' };
const JAAR = 2026;

const euro = (n, opties = {}) => (n === null || n === undefined || Number.isNaN(n)
  ? '—'
  : new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0, ...opties }).format(n));
const kEuro = (n) => (n === null || n === undefined ? '—' : `€${Math.round(n / 1000)}k`);
const procent = (f) => (f === null || f === undefined ? '—' : `${Math.round(f * 100)}%`);
const MAAND_LANG = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];

/** Cumulatief: gerealiseerd (lijn met punten) tegen het doelpad (stippellijn). */
function CumulatiefGrafiek({ rijen, doel }) {
  const B = 720; const H = 260; const L = 64; const R = 16; const T = 16; const O = 40;
  const max = Math.max(doel || 0, ...rijen.map((r) => r.cumulatief || 0), ...rijen.map((r) => r.cumulatiefPad || 0), 1);
  const x = (i) => L + (i / 11) * (B - L - R);
  const y = (v) => T + (1 - v / max) * (H - T - O);
  const pad = rijen.map((r, i) => `${x(i)},${y(r.cumulatiefPad)}`).join(' ');
  const gerealiseerd = rijen.filter((r) => r.cumulatief !== null);
  const lijn = gerealiseerd.map((r) => `${x(rijen.indexOf(r))},${y(r.cumulatief)}`).join(' ');
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);
  return (
    <svg viewBox={`0 0 ${B} ${H}`} role="img" aria-label="Cumulatieve BBI tegenover het doelpad"
      style={{ width: '100%', height: 'auto', display: 'block', fontFamily: NAV_FONT }}>
      {ticks.map((t) => (
        <g key={t}>
          <line x1={L} x2={B - R} y1={y(t)} y2={y(t)} stroke="#e5e7eb" />
          <text x={L - 8} y={y(t) + 4} textAnchor="end" fontSize="11" fill="#94a3b8">{kEuro(t)}</text>
        </g>
      ))}
      {doel > 0 && (
        <g>
          <line x1={L} x2={B - R} y1={y(doel)} y2={y(doel)} stroke={ACCENT.color} strokeWidth="1.5" strokeDasharray="2 4" />
          <text x={B - R} y={y(doel) - 6} textAnchor="end" fontSize="11" fontWeight="800" fill={ACCENT.color}>doel {kEuro(doel)}</text>
        </g>
      )}
      <polyline points={pad} fill="none" stroke="#94a3b8" strokeWidth="2" strokeDasharray="6 5" />
      {lijn && <polyline points={lijn} fill="none" stroke="#1e3a8a" strokeWidth="3" strokeLinejoin="round" />}
      {gerealiseerd.map((r) => (
        <circle key={r.code} cx={x(rijen.indexOf(r))} cy={y(r.cumulatief)} r="4.5" fill="#1e3a8a" />
      ))}
      {rijen.map((r, i) => (
        <text key={r.code} x={x(i)} y={H - 14} textAnchor="middle" fontSize="11" fill="#64748b">{r.naam}</text>
      ))}
    </svg>
  );
}

/** Presentatie-sheet: één A4-liggend, alleen wat het personeel hoort te zien. */
function openSheet(doc) {
  const a = doc.afgeleid;
  const rijen = a.rijen;
  const maxMaand = Math.max(...rijen.map((r) => Math.max(r.bbi || 0, r.pad || 0)), 1);
  const staafjes = rijen.map((r) => {
    const hA = r.bbi ? Math.round((r.bbi / maxMaand) * 120) : 0;
    const hP = Math.round(((r.pad || 0) / maxMaand) * 120);
    return `<div class="m"><div class="bars"><div class="p" style="height:${hP}px" title="pad ${kEuro(r.pad)}"></div><div class="a" style="height:${hA}px" title="${kEuro(r.bbi)}"></div></div><div class="lbl">${r.naam}</div><div class="val">${r.bbi ? kEuro(r.bbi) : ''}</div></div>`;
  }).join('');
  const voortgang = a.voortgang ? Math.min(100, Math.round(a.voortgang * 100)) : 0;
  const stand = a.laatsteMaandNaam ? `t/m ${MAAND_LANG[rijen.findIndex((r) => r.code === a.laatsteMaand)]}` : 'nog geen maand ingevuld';
  const verschil = a.verschilPad;
  const verschilTekst = verschil === null || verschil === undefined ? ''
    : `${verschil >= 0 ? '+' : '−'}${euro(Math.abs(verschil))} t.o.v. ${a.padType === 'budget' ? 'het maandbudget' : 'een gelijkmatig pad'}`;
  const laatsteNotitie = [...rijen].reverse().find((r) => r.notitie)?.notitie || '';
  const html = `<!doctype html><html lang="nl"><head><meta charset="utf-8"><title>BBI-doel ${doc.jaar}</title>
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Manrope:wght@600;800&family=Inter:wght@400;600&display=swap">
<style>
@page{size:A4 landscape;margin:14mm}
body{font-family:Inter,system-ui,sans-serif;color:#0f172a;margin:0;-webkit-print-color-adjust:exact;print-color-adjust:exact}
h1,.n,.lbl{font-family:Manrope,system-ui,sans-serif}
.kop{display:flex;justify-content:space-between;align-items:baseline;border-bottom:2px solid #0f172a;padding-bottom:10px;margin-bottom:22px}
h1{font-size:28px;margin:0;font-weight:800;letter-spacing:-.01em}
.sub{color:#64748b;font-size:13px}
.tiles{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:22px}
.tile{border:1px solid #e2e8f0;padding:14px 16px}
.tile .l{font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:#64748b;font-weight:600}
.tile .n{font-size:30px;font-weight:800;letter-spacing:-.02em;margin-top:4px;font-variant-numeric:tabular-nums}
.tile .s{font-size:12px;color:#64748b;margin-top:2px}
.balk{height:26px;background:#e2e8f0;border-radius:4px;overflow:hidden;position:relative;margin:6px 0 26px}
.balk>div{height:100%;background:${ACCENT.color};width:${voortgang}%}
.balk span{position:absolute;left:12px;top:4px;color:#fff;font-weight:800;font-family:Manrope,sans-serif;font-size:14px}
.chart{display:grid;grid-template-columns:repeat(12,1fr);gap:8px;align-items:end;border-bottom:1px solid #cbd5e1;padding-bottom:6px}
.m{text-align:center}.bars{display:flex;justify-content:center;align-items:flex-end;gap:3px;height:120px}
.bars .a{width:22px;background:#1e3a8a}.bars .p{width:8px;background:#cbd5e1}
.lbl{font-size:12px;font-weight:600;color:#475569;margin-top:6px}.val{font-size:11px;color:#1e3a8a;font-weight:600}
.legenda{font-size:12px;color:#64748b;margin-top:10px;display:flex;gap:18px}
.legenda i{display:inline-block;width:12px;height:12px;vertical-align:-2px;margin-right:6px}
.notitie{margin-top:18px;font-size:13px;color:#334155;max-width:70ch}
.voet{margin-top:18px;font-size:11px;color:#94a3b8}
</style></head><body>
<div class="kop"><h1>Samen naar ${euro(doc.doel)} BBI in ${doc.jaar}</h1><div class="sub">stand ${stand}</div></div>
<div class="balk"><div></div><span>${voortgang}% van het doel</span></div>
<div class="tiles">
 <div class="tile"><div class="l">Doel ${doc.jaar}</div><div class="n">${euro(doc.doel)}</div><div class="s">brutobijdrage (BBI)</div></div>
 <div class="tile"><div class="l">Gerealiseerd</div><div class="n">${euro(a.gerealiseerd)}</div><div class="s">${verschilTekst}</div></div>
 <div class="tile"><div class="l">Nog nodig</div><div class="n">${euro(a.resterend)}</div><div class="s">in ${a.maandenOver} maand${a.maandenOver === 1 ? '' : 'en'}</div></div>
 <div class="tile"><div class="l">Nodig per maand</div><div class="n">${euro(a.nodigPerMaand)}</div><div class="s">tot nu gemiddeld ${euro(a.gemiddeldPerMaand)}</div></div>
</div>
<div class="chart">${staafjes}</div>
<div class="legenda"><span><i style="background:#1e3a8a"></i>BBI per maand</span><span><i style="background:#cbd5e1"></i>${a.padType === 'budget' ? 'maandbudget' : 'gelijkmatig pad (doel ÷ 12)'}</span></div>
${laatsteNotitie ? `<div class="notitie">${laatsteNotitie.replace(/</g, '&lt;')}</div>` : ''}
<div class="voet">BBI = netto omzet min inkoop. Bron: maandrapportage Only. Opgesteld ${new Date().toLocaleDateString('nl-NL')}.</div>
<script>window.onload=function(){setTimeout(function(){window.print()},400)}</script>
</body></html>`;
  const w = window.open('', '_blank');
  if (!w) { toast.error('Pop-up geblokkeerd — sta pop-ups toe om de sheet te openen.'); return; }
  w.document.write(html);
  w.document.close();
}

export default function BbiDoel() {
  const { permissions } = useUserRole();
  const magBewerken = !!permissions?.kpi_dashboard?.edit;
  const [doc, setDoc] = useState(null);
  const [concept, setConcept] = useState(null); // bewerkbare kopie: { doel, maanden }
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState(null);

  const laad = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/bbi/doel?jaar=${JAAR}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || d.detail || `HTTP ${r.status}`);
      setDoc(d);
      setConcept({ doel: d.doel ?? '', maanden: d.maanden });
    } catch (e) { setFout(e.message); }
  }, []);
  useEffect(() => { laad(); }, [laad]);

  const gewijzigd = useMemo(() => {
    if (!doc || !concept) return false;
    return JSON.stringify({ doel: doc.doel ?? '', maanden: doc.maanden }) !== JSON.stringify(concept);
  }, [doc, concept]);

  const zetMaand = (code, veld, waarde) => setConcept((c) => ({
    ...c, maanden: { ...c.maanden, [code]: { ...c.maanden[code], [veld]: waarde } },
  }));

  const bewaar = async () => {
    setBezig(true);
    try {
      const body = {
        doel: concept.doel === '' ? null : Number(concept.doel),
        maanden: Object.fromEntries(Object.entries(concept.maanden).map(([code, m]) => [code, {
          bbi: m.bbi === '' || m.bbi === null ? null : Number(m.bbi),
          budget: m.budget === '' || m.budget === null ? null : Number(m.budget),
          notitie: m.notitie || '',
        }])),
      };
      const r = await authFetch(`${BACKEND_URL}/Api/bbi/doel?jaar=${JAAR}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || d.detail || `HTTP ${r.status}`);
      setDoc(d);
      setConcept({ doel: d.doel ?? '', maanden: d.maanden });
      toast.success('Opgeslagen');
    } catch (e) { toast.error(`Opslaan mislukt: ${e.message}`); } finally { setBezig(false); }
  };

  const a = doc?.afgeleid;
  const tegel = (label, waarde, sub, kleur) => (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 2, p: 2 }}>
      <Typography sx={{ fontFamily: NAV_FONT, fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#64748b' }}>{label}</Typography>
      <Typography sx={{ fontFamily: NAV_FONT, fontSize: 30, fontWeight: 800, letterSpacing: '-0.02em', color: kleur || '#0f172a', fontVariantNumeric: 'tabular-nums', lineHeight: 1.15, mt: 0.5 }}>{waarde}</Typography>
      {sub && <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b', mt: 0.25 }}>{sub}</Typography>}
    </Box>
  );

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader
        icon="flag"
        title={`BBI-doel ${JAAR}`}
        subtitle="Brutobijdrage volgens de maandrapportage van Only, afgezet tegen het bonusdoel"
        accent={ACCENT}
        rightActions={(
          <Box sx={{ display: 'flex', gap: 1 }}>
            {magBewerken && (
              <Button size="small" variant="contained" startIcon={bezig ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
                disabled={!gewijzigd || bezig} onClick={bewaar} sx={{ textTransform: 'none', fontWeight: 700 }}>
                Opslaan
              </Button>
            )}
            <Button size="small" variant="outlined" startIcon={<PrintIcon sx={{ fontSize: 16 }} />}
              disabled={!doc?.doel} onClick={() => openSheet(doc)} sx={{ textTransform: 'none', fontWeight: 700 }}
              title="Eén A4 voor het personeel: doel, stand en tempo — zonder kosten of winst">
              Presentatie-sheet
            </Button>
          </Box>
        )}
      />

      {fout && <Typography color="error" sx={{ mb: 2 }}>{fout}</Typography>}
      {!doc && !fout && <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={24} /></Box>}

      {doc && a && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 3 }}>
            {tegel(`Doel ${JAAR}`, euro(doc.doel), 'brutobijdrage (BBI)')}
            {tegel('Gerealiseerd', euro(a.gerealiseerd), a.laatsteMaandNaam ? `t/m ${a.laatsteMaandNaam} · ${procent(a.voortgang)} van het doel` : 'nog geen maand ingevuld')}
            {tegel('Nog nodig', euro(a.resterend), a.maandenOver ? `in ${a.maandenOver} maanden` : 'jaar is vol', a.resterend !== null && a.resterend <= 0 ? ACCENT.color : undefined)}
            {tegel('Nodig per maand', euro(a.nodigPerMaand), `tot nu gemiddeld ${euro(a.gemiddeldPerMaand)} · op dit tempo ${euro(a.prognoseOpTempo)}`,
              a.nodigPerMaand !== null && a.gemiddeldPerMaand !== null && a.nodigPerMaand > a.gemiddeldPerMaand ? '#b45309' : undefined)}
          </Box>

          <Box sx={{ bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 2, p: { xs: 1.5, md: 2.5 }, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography sx={{ fontFamily: NAV_FONT, fontWeight: 800, fontSize: 15 }}>Cumulatief tegenover het doelpad</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: a.padType === 'budget' ? '#64748b' : '#b45309' }}>
                {a.padType === 'budget'
                  ? 'Pad = maandbudget van Only'
                  : 'Pad = doel ÷ 12 (vul het maandbudget van Only in voor een seizoenspad)'}
                {a.verschilPad !== null && ` · nu ${a.verschilPad >= 0 ? '+' : '−'}${euro(Math.abs(a.verschilPad))} t.o.v. pad`}
              </Typography>
            </Box>
            <CumulatiefGrafiek rijen={a.rijen} doel={doc.doel || 0} />
          </Box>

          <Box sx={{ bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden', mb: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '90px 150px 150px 140px 140px 1fr', gap: 1, px: 2, py: 1, bgcolor: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
              {['Maand', 'Budget (Only)', 'BBI (Only)', 'Cumulatief', 'T.o.v. pad', 'Notitie'].map((k) => (
                <Typography key={k} sx={{ fontFamily: NAV_FONT, fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#64748b' }}>{k}</Typography>
              ))}
            </Box>
            {a.rijen.map((r) => {
              const c = concept.maanden[r.code] || {};
              const invoer = (veld, breed) => (magBewerken ? (
                <TextField size="small" type={veld === 'notitie' ? 'text' : 'number'} value={c[veld] ?? ''} fullWidth={breed}
                  placeholder={veld === 'notitie' ? 'bv. wegvallen ING, tender gemist' : '€'}
                  onChange={(e) => zetMaand(r.code, veld, e.target.value)}
                  inputProps={{ style: { fontSize: 13, padding: '5px 8px', fontVariantNumeric: 'tabular-nums' } }} />
              ) : (
                <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontVariantNumeric: 'tabular-nums' }}>
                  {veld === 'notitie' ? (r.notitie || '') : euro(r[veld])}
                </Typography>
              ));
              return (
                <Box key={r.code} sx={{ display: 'grid', gridTemplateColumns: '90px 150px 150px 140px 140px 1fr', gap: 1, alignItems: 'center', px: 2, py: 0.75, borderBottom: '1px solid #f1f5f9' }}>
                  <Typography sx={{ fontFamily: NAV_FONT, fontWeight: 700, fontSize: 13.5, textTransform: 'capitalize' }}>{r.naam}</Typography>
                  {invoer('budget')}
                  {invoer('bbi')}
                  <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontVariantNumeric: 'tabular-nums', color: '#0f172a' }}>{euro(r.cumulatief)}</Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontVariantNumeric: 'tabular-nums', fontWeight: 700,
                    color: r.verschilPad === null ? '#94a3b8' : r.verschilPad >= 0 ? ACCENT.color : '#b91c1c' }}>
                    {r.verschilPad === null ? '—' : `${r.verschilPad >= 0 ? '+' : '−'}${euro(Math.abs(r.verschilPad))}`}
                  </Typography>
                  {invoer('notitie', true)}
                </Box>
              );
            })}
            {magBewerken && (
              <Box sx={{ display: 'grid', gridTemplateColumns: '90px 240px 1fr', gap: 1, alignItems: 'center', px: 2, py: 1.25, bgcolor: '#f8fafc' }}>
                <Typography sx={{ fontFamily: NAV_FONT, fontWeight: 800, fontSize: 13.5 }}>Doel</Typography>
                <TextField size="small" type="number" value={concept.doel} onChange={(e) => setConcept((k) => ({ ...k, doel: e.target.value }))}
                  inputProps={{ style: { fontSize: 13, padding: '5px 8px', fontWeight: 700 } }} />
                <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>
                  Jaardoel BBI in euro's. Budget en BBI per maand komen uit de maandrapportage van Only (tab "vs. budget"/"vs. reforecast", regel BBI, kolom AC).
                </Typography>
              </Box>
            )}
          </Box>

          <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#94a3b8' }}>
            {doc.bijgewerkt ? `Laatst bijgewerkt ${new Date(doc.bijgewerkt).toLocaleString('nl-NL')}${doc.door ? ` door ${doc.door}` : ''}. ` : ''}
            BBI = netto omzet − inkoop. Bewust niet live uit Gripp berekend: die mist de inkoopkant en wijkt per maand tot ruim €100k af van wat Only boekt.
          </Typography>
        </>
      )}
    </Box>
  );
}
